import {
  Alert,
  Button,
  Card,
  Checkbox,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tabs,
  Tag,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationGradeSelect,
  EducationSectionSelect,
  EducationSessionSelect,
  EducationStaffSelect,
  EducationSubjectSelect,
} from '@/pages/education/components/EducationLookupSelects';

type ApiError = { response?: { data?: { message?: string } } };

type SessionRow = {
  id: string;
  code: string;
  name: string;
  start_date: string;
  end_date: string;
  status: string;
};

type GradeRow = { id: string; code: string; name: string; sequence_no: number; record_version?: number };
type SectionRow = {
  id: string;
  name: string;
  capacity: number;
  enrolled_count?: number;
  seats_available?: number;
  grade_level_id?: string;
  grade_code?: string;
  grade_name?: string;
  session_id?: string;
  session_code?: string;
  record_version?: number;
};
type SubjectRow = {
  id: string;
  code: string;
  name: string;
  offering_count?: number;
  record_version?: number;
  hod_staff_link_id?: string | null;
  hod_employee_code?: string | null;
  hod_first_name?: string | null;
  hod_last_name?: string | null;
};
type OfferingRow = {
  id: string;
  subject_id?: string;
  grade_level_id?: string;
  session_id?: string;
  subject_code?: string;
  subject_name?: string;
  grade_code?: string;
  session_code?: string;
  is_elective?: number | boolean;
  record_version?: number;
};
type TeacherRow = {
  id: string;
  section_id?: string;
  subject_id?: string;
  staff_link_id?: string;
  section_name?: string;
  grade_code?: string;
  subject_code?: string;
  subject_name?: string;
  employee_code?: string;
  first_name?: string;
  last_name?: string;
  session_id?: string;
  session_code?: string;
};

function asList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: T[] }).data;
  }
  return [];
}

export default function AcademicSessionsPage() {
  const { t } = useTranslation('education');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(user?.permissions?.includes('education.academic.manage'));
  const tab = params.get('tab') || 'sessions';
  const sessionFilter = params.get('session') || undefined;

  const [gradeForm] = Form.useForm<{ code: string; name: string; sequence_no: number }>();
  const [sectionForm] = Form.useForm<{
    session_id: string;
    grade_level_id: string;
    name: string;
    capacity: number;
  }>();
  const [subjectForm] = Form.useForm<{ code: string; name: string; hod_staff_link_id?: string }>();
  const [offeringForm] = Form.useForm<{
    session_id: string;
    grade_level_id: string;
    subject_id: string;
    is_elective?: boolean;
  }>();
  const [teacherForm] = Form.useForm<{ section_id: string; subject_id: string; staff_link_id: string }>();

  const [gradeModal, setGradeModal] = useState<'create' | GradeRow | null>(null);
  const [sectionModal, setSectionModal] = useState<'create' | SectionRow | null>(null);
  const [subjectModal, setSubjectModal] = useState<'create' | SubjectRow | null>(null);
  const [offeringModal, setOfferingModal] = useState<'create' | OfferingRow | null>(null);
  const [teacherModal, setTeacherModal] = useState(false);

  const sessions = useQuery({
    queryKey: ['education-academic-sessions'],
    queryFn: async () => asList<SessionRow>((await api.get('/education/academic/sessions')).data),
  });

  const grades = useQuery({
    queryKey: ['education-academic-grades-admin'],
    enabled: tab === 'grades' || tab === 'subjects' || Boolean(sectionModal) || Boolean(offeringModal),
    queryFn: async () => asList<GradeRow>((await api.get('/education/academic/grades')).data),
  });

  const sections = useQuery({
    queryKey: ['education-academic-sections-admin', sessionFilter],
    enabled: tab === 'grades',
    queryFn: async () =>
      asList<SectionRow>(
        (await api.get('/education/academic/sections', { params: { session_id: sessionFilter || undefined } })).data
      ),
  });

  const subjects = useQuery({
    queryKey: ['education-academic-subjects-admin'],
    enabled: tab === 'subjects' || Boolean(offeringModal) || Boolean(teacherModal),
    queryFn: async () => asList<SubjectRow>((await api.get('/education/academic/subjects')).data),
  });

  const offerings = useQuery({
    queryKey: ['education-academic-offerings-admin', sessionFilter],
    enabled: tab === 'subjects',
    queryFn: async () =>
      asList<OfferingRow>(
        (await api.get('/education/academic/offerings', { params: { session_id: sessionFilter || undefined } })).data
      ),
  });

  const teachers = useQuery({
    queryKey: ['education-academic-teachers-admin', sessionFilter],
    enabled: tab === 'teachers',
    queryFn: async () =>
      asList<TeacherRow>(
        (
          await api.get('/education/academic/section-teachers', {
            params: { session_id: sessionFilter || undefined },
          })
        ).data
      ),
  });

  const staff = useQuery({
    queryKey: ['education-staff-admin'],
    enabled: tab === 'teachers',
    queryFn: async () =>
      asList<{
        id: string;
        employee_code?: string;
        first_name?: string;
        last_name?: string;
        section_assignment_count?: number;
        student_assignment_count?: number;
        is_active?: number;
      }>((await api.get('/education/staff')).data),
  });

  const activeSession = useMemo(
    () => (sessions.data ?? []).find((row) => row.status === 'active') || (sessions.data ?? [])[0],
    [sessions.data]
  );

  const invalidateAcademic = () => {
    queryClient.invalidateQueries({ queryKey: ['education-academic-sessions'] });
    queryClient.invalidateQueries({ queryKey: ['education-academic-grades-admin'] });
    queryClient.invalidateQueries({ queryKey: ['education-academic-sections-admin'] });
    queryClient.invalidateQueries({ queryKey: ['education-academic-subjects-admin'] });
    queryClient.invalidateQueries({ queryKey: ['education-academic-offerings-admin'] });
    queryClient.invalidateQueries({ queryKey: ['education-academic-teachers-admin'] });
    queryClient.invalidateQueries({ queryKey: ['education-staff-admin'] });
    queryClient.invalidateQueries({ queryKey: ['education-lookup-grades'] });
    queryClient.invalidateQueries({ queryKey: ['education-lookup-sections'] });
    queryClient.invalidateQueries({ queryKey: ['education-lookup-subjects'] });
    queryClient.invalidateQueries({ queryKey: ['education-lookup-sessions'] });
    queryClient.invalidateQueries({ queryKey: ['education-lookup-staff'] });
  };

  const onError = (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' }));

  const saveGrade = useMutation({
    mutationFn: async (values: { code: string; name: string; sequence_no: number }) => {
      if (gradeModal && gradeModal !== 'create') {
        return api.put(`/education/academic/grades/${gradeModal.id}`, {
          ...values,
          record_version: gradeModal.record_version ?? 1,
        });
      }
      return api.post('/education/academic/grades', values);
    },
    onSuccess: () => {
      message.success(t('saved'));
      setGradeModal(null);
      gradeForm.resetFields();
      invalidateAcademic();
    },
    onError,
  });

  const deleteGrade = useMutation({
    mutationFn: (id: string) => api.delete(`/education/academic/grades/${id}`),
    onSuccess: () => {
      message.success(t('deleted', { defaultValue: 'Deleted' }));
      invalidateAcademic();
    },
    onError,
  });

  const saveSection = useMutation({
    mutationFn: async (values: {
      session_id: string;
      grade_level_id: string;
      name: string;
      capacity: number;
    }) => {
      if (sectionModal && sectionModal !== 'create') {
        return api.put(`/education/academic/sections/${sectionModal.id}`, {
          name: values.name,
          capacity: values.capacity,
          grade_level_id: values.grade_level_id,
          record_version: sectionModal.record_version ?? 1,
        });
      }
      return api.post('/education/academic/sections', values);
    },
    onSuccess: () => {
      message.success(t('saved'));
      setSectionModal(null);
      sectionForm.resetFields();
      invalidateAcademic();
    },
    onError,
  });

  const deleteSection = useMutation({
    mutationFn: (id: string) => api.delete(`/education/academic/sections/${id}`),
    onSuccess: () => {
      message.success(t('deleted', { defaultValue: 'Deleted' }));
      invalidateAcademic();
    },
    onError,
  });

  const saveSubject = useMutation({
    mutationFn: async (values: { code: string; name: string; hod_staff_link_id?: string }) => {
      const payload = {
        code: values.code,
        name: values.name,
        hod_staff_link_id: values.hod_staff_link_id || null,
      };
      if (subjectModal && subjectModal !== 'create') {
        return api.put(`/education/academic/subjects/${subjectModal.id}`, {
          ...payload,
          record_version: subjectModal.record_version ?? 1,
        });
      }
      return api.post('/education/academic/subjects', payload);
    },
    onSuccess: () => {
      message.success(t('saved'));
      setSubjectModal(null);
      subjectForm.resetFields();
      invalidateAcademic();
    },
    onError,
  });

  const deleteSubject = useMutation({
    mutationFn: (id: string) => api.delete(`/education/academic/subjects/${id}`),
    onSuccess: () => {
      message.success(t('deleted', { defaultValue: 'Deleted' }));
      invalidateAcademic();
    },
    onError,
  });

  const saveOffering = useMutation({
    mutationFn: async (values: {
      session_id: string;
      grade_level_id: string;
      subject_id: string;
      is_elective?: boolean;
    }) => {
      if (offeringModal && offeringModal !== 'create') {
        return api.put(`/education/academic/offerings/${offeringModal.id}`, {
          is_elective: values.is_elective,
          record_version: offeringModal.record_version ?? 1,
        });
      }
      return api.post('/education/academic/offerings', values);
    },
    onSuccess: () => {
      message.success(t('saved'));
      setOfferingModal(null);
      offeringForm.resetFields();
      invalidateAcademic();
    },
    onError,
  });

  const deleteOffering = useMutation({
    mutationFn: (id: string) => api.delete(`/education/academic/offerings/${id}`),
    onSuccess: () => {
      message.success(t('deleted', { defaultValue: 'Deleted' }));
      invalidateAcademic();
    },
    onError,
  });

  const assignTeacher = useMutation({
    mutationFn: (values: { section_id: string; subject_id: string; staff_link_id: string }) =>
      api.post('/education/academic/section-teachers', values),
    onSuccess: () => {
      message.success(t('teacherAssigned', { defaultValue: 'Teacher assigned' }));
      setTeacherModal(false);
      teacherForm.resetFields();
      invalidateAcademic();
    },
    onError,
  });

  const unassignTeacher = useMutation({
    mutationFn: (id: string) => api.delete(`/education/academic/section-teachers/${id}`),
    onSuccess: () => {
      message.success(t('deleted', { defaultValue: 'Deleted' }));
      invalidateAcademic();
    },
    onError,
  });

  const setTab = (key: string) => {
    const next = new URLSearchParams(params);
    if (key === 'sessions') next.delete('tab');
    else next.set('tab', key);
    setParams(next);
  };

  const setSessionFilter = (value?: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set('session', value);
    else next.delete('session');
    setParams(next);
  };

  const openSessionWorkspace = (sessionId: string, panel?: string) => {
    const q = panel ? `?panel=${panel}` : '';
    navigate(`/education/academic/sessions/${sessionId}${q}`);
  };

  const openGradeCreate = () => {
    gradeForm.resetFields();
    setGradeModal('create');
  };
  const openGradeEdit = (row: GradeRow) => {
    gradeForm.setFieldsValue({ code: row.code, name: row.name, sequence_no: row.sequence_no });
    setGradeModal(row);
  };
  const openSectionCreate = () => {
    sectionForm.setFieldsValue({
      session_id: sessionFilter || activeSession?.id,
      capacity: 40,
    });
    setSectionModal('create');
  };
  const openSectionEdit = (row: SectionRow) => {
    setSectionModal(row);
    // Set after open so Select options are mounted (avoids raw UUID label).
    window.setTimeout(() => {
      sectionForm.setFieldsValue({
        session_id: row.session_id,
        grade_level_id: row.grade_level_id,
        name: row.name,
        capacity: row.capacity,
      });
    }, 0);
  };
  const openSubjectCreate = () => {
    subjectForm.resetFields();
    setSubjectModal('create');
  };
  const openSubjectEdit = (row: SubjectRow) => {
    setSubjectModal(row);
    window.setTimeout(() => {
      subjectForm.setFieldsValue({
        code: row.code,
        name: row.name,
        hod_staff_link_id: row.hod_staff_link_id || undefined,
      });
    }, 0);
  };

  const hodLabel = (row: SubjectRow) => {
    const name = [row.hod_first_name, row.hod_last_name].filter(Boolean).join(' ').trim();
    if (row.hod_employee_code && name) return `${row.hod_employee_code} — ${name}`;
    return name || row.hod_employee_code || '—';
  };
  const openOfferingCreate = () => {
    offeringForm.setFieldsValue({
      session_id: sessionFilter || activeSession?.id,
      is_elective: false,
    });
    setOfferingModal('create');
  };
  const openOfferingEdit = (row: OfferingRow) => {
    offeringForm.setFieldsValue({
      session_id: row.session_id,
      grade_level_id: row.grade_level_id,
      subject_id: row.subject_id,
      is_elective: Boolean(row.is_elective),
    });
    setOfferingModal(row);
  };

  const title =
    tab === 'grades'
      ? t('gradesSectionsTitle', { defaultValue: 'Grades & sections' })
      : tab === 'subjects'
        ? t('subjectsOfferingsTitle', { defaultValue: 'Subjects & offerings' })
        : tab === 'teachers'
          ? t('teacherAssignments', { defaultValue: 'Teacher assignments' })
          : t('academicTitle');

  const editingOffering = offeringModal && offeringModal !== 'create';

  return (
    <EducationPageChrome
      title={title}
      description={t('academicAdminIntro', {
        defaultValue:
          'Configure the school year structure for a multi-section campus: sessions, terms, grades, class sections, subject offerings, and section teachers. Does not invent exams or fees.',
      })}
      tags={<Tag>{(sessions.data ?? []).length} {t('hubStatSessions')}</Tag>}
      extra={
        <Space wrap>
          {activeSession ? (
            <Button onClick={() => openSessionWorkspace(activeSession.id)}>
              {t('openActiveSession', { defaultValue: 'Open active session' })}
            </Button>
          ) : null}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/education/academic/sessions/new')}>
            {t('newSession')}
          </Button>
        </Space>
      }
    >
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'sessions',
            label: t('academicTitle'),
            children: (
              <Card size="small">
                <Table
                  rowKey="id"
                  loading={sessions.isLoading}
                  dataSource={sessions.data ?? []}
                  pagination={{ pageSize: 20, showSizeChanger: true }}
                  locale={{
                    emptyText: (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('setupMissingAcademic')}>
                        <Button type="primary" onClick={() => navigate('/education/academic/sessions/new')}>
                          {t('newSession')}
                        </Button>
                      </Empty>
                    ),
                  }}
                  onRow={(row) => ({
                    onClick: () => openSessionWorkspace(row.id),
                    style: { cursor: 'pointer' },
                  })}
                  columns={[
                    { title: t('sessionCode'), dataIndex: 'code', width: 140 },
                    { title: t('sessionName'), dataIndex: 'name' },
                    { title: t('startDate'), dataIndex: 'start_date', width: 130 },
                    { title: t('endDate'), dataIndex: 'end_date', width: 130 },
                    {
                      title: t('status'),
                      dataIndex: 'status',
                      width: 120,
                      render: (status: string) => (
                        <Tag color={status === 'active' ? 'success' : status === 'closed' ? 'default' : 'orange'}>{status}</Tag>
                      ),
                    },
                    {
                      title: t('actions', { defaultValue: 'Actions' }),
                      key: 'actions',
                      width: 160,
                      render: (_: unknown, row: SessionRow) => (
                        <Button
                          size="small"
                          type="link"
                          onClick={(event) => {
                            event.stopPropagation();
                            openSessionWorkspace(row.id);
                          }}
                        >
                          {t('openWorkspace', { defaultValue: 'Open workspace' })}
                        </Button>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'grades',
            label: t('gradesSectionsTitle', { defaultValue: 'Grades & sections' }),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert
                  type="info"
                  showIcon
                  message={t('gradesTabHint', {
                    defaultValue:
                      'Grades are school-wide. Sections belong to a session. Create, edit, or delete here, or open a session workspace for bulk A–F create.',
                  })}
                  action={
                    sessionFilter || activeSession ? (
                      <Button
                        size="small"
                        onClick={() => openSessionWorkspace(sessionFilter || activeSession!.id, 'sections')}
                      >
                        {t('manageInSession', { defaultValue: 'Manage in session' })}
                      </Button>
                    ) : null
                  }
                />
                <Space wrap>
                  <EducationSessionSelect
                    allowClear
                    placeholder={t('filterBySession', { defaultValue: 'Filter sections by session' })}
                    value={sessionFilter}
                    onChange={(value) => setSessionFilter(value || undefined)}
                    style={{ width: 320 }}
                  />
                  {canManage ? (
                    <>
                      <Button type="primary" icon={<PlusOutlined />} onClick={openGradeCreate}>
                        {t('addGrade')}
                      </Button>
                      <Button icon={<PlusOutlined />} onClick={openSectionCreate}>
                        {t('addSection')}
                      </Button>
                    </>
                  ) : null}
                </Space>
                <Card size="small" title={t('grades', { defaultValue: 'Grades' })}>
                  <Table
                    rowKey="id"
                    size="small"
                    loading={grades.isLoading}
                    dataSource={grades.data ?? []}
                    pagination={false}
                    columns={[
                      { title: t('gradeCode'), dataIndex: 'code', width: 100 },
                      { title: t('gradeName'), dataIndex: 'name' },
                      { title: t('sequence'), dataIndex: 'sequence_no', width: 100 },
                      ...(canManage
                        ? [
                            {
                              title: t('actions', { defaultValue: 'Actions' }),
                              key: 'actions',
                              width: 180,
                              render: (_: unknown, row: GradeRow) => (
                                <Space>
                                  <Button size="small" onClick={() => openGradeEdit(row)}>
                                    {tc('edit')}
                                  </Button>
                                  <Popconfirm
                                    title={t('confirmDeleteGrade', {
                                      defaultValue: 'Delete this grade? Sections and offerings must be removed first.',
                                    })}
                                    onConfirm={() => deleteGrade.mutate(row.id)}
                                  >
                                    <Button size="small" danger loading={deleteGrade.isPending}>
                                      {tc('delete')}
                                    </Button>
                                  </Popconfirm>
                                </Space>
                              ),
                            },
                          ]
                        : []),
                    ]}
                  />
                </Card>
                <Card size="small" title={t('sectionsForSession', { defaultValue: 'Sections' })}>
                  <Table
                    rowKey="id"
                    size="small"
                    loading={sections.isLoading}
                    dataSource={sections.data ?? []}
                    pagination={{ pageSize: 20 }}
                    locale={{ emptyText: t('noSectionsYet') }}
                    columns={[
                      { title: t('session'), dataIndex: 'session_code', width: 120 },
                      {
                        title: t('grade'),
                        key: 'grade',
                        render: (_: unknown, row: SectionRow) => row.grade_name || row.grade_code || '—',
                      },
                      { title: t('section'), dataIndex: 'name', width: 90 },
                      { title: t('capacity'), dataIndex: 'capacity', width: 90 },
                      {
                        title: t('enrolled', { defaultValue: 'Enrolled' }),
                        dataIndex: 'enrolled_count',
                        width: 90,
                        render: (v?: number) => v ?? 0,
                      },
                      {
                        title: t('seatsLeft', { defaultValue: 'Seats left' }),
                        dataIndex: 'seats_available',
                        width: 100,
                        render: (v?: number) => <Tag color={(v ?? 0) > 0 ? 'green' : 'red'}>{v ?? 0}</Tag>,
                      },
                      ...(canManage
                        ? [
                            {
                              title: t('actions', { defaultValue: 'Actions' }),
                              key: 'actions',
                              width: 180,
                              render: (_: unknown, row: SectionRow) => (
                                <Space>
                                  <Button size="small" onClick={() => openSectionEdit(row)}>
                                    {tc('edit')}
                                  </Button>
                                  <Popconfirm
                                    title={t('confirmDeleteSection', {
                                      defaultValue: 'Delete this section? Enrollments must be empty.',
                                    })}
                                    onConfirm={() => deleteSection.mutate(row.id)}
                                  >
                                    <Button size="small" danger loading={deleteSection.isPending}>
                                      {tc('delete')}
                                    </Button>
                                  </Popconfirm>
                                </Space>
                              ),
                            },
                          ]
                        : []),
                    ]}
                  />
                </Card>
              </Space>
            ),
          },
          {
            key: 'subjects',
            label: t('subjectsOfferingsTitle', { defaultValue: 'Subjects & offerings' }),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert
                  type="info"
                  showIcon
                  message={t('subjectsTabHint', {
                    defaultValue:
                      'Maintain the subject catalogue once, then offer subjects per session and grade. Teachers can only be assigned to offered subjects.',
                  })}
                  action={
                    sessionFilter || activeSession ? (
                      <Button
                        size="small"
                        onClick={() => openSessionWorkspace(sessionFilter || activeSession!.id, 'subjects')}
                      >
                        {t('manageInSession', { defaultValue: 'Manage in session' })}
                      </Button>
                    ) : null
                  }
                />
                <Space wrap>
                  <EducationSessionSelect
                    allowClear
                    placeholder={t('filterBySession', { defaultValue: 'Filter offerings by session' })}
                    value={sessionFilter}
                    onChange={(value) => setSessionFilter(value || undefined)}
                    style={{ width: 320 }}
                  />
                  {canManage ? (
                    <>
                      <Button type="primary" icon={<PlusOutlined />} onClick={openSubjectCreate}>
                        {t('addSubject')}
                      </Button>
                      <Button icon={<PlusOutlined />} onClick={openOfferingCreate}>
                        {t('addOffering', { defaultValue: 'Add offering' })}
                      </Button>
                    </>
                  ) : null}
                </Space>
                <Card size="small" title={t('subjectCatalogue', { defaultValue: 'Subject catalogue' })}>
                  <Table
                    rowKey="id"
                    size="small"
                    loading={subjects.isLoading}
                    dataSource={subjects.data ?? []}
                    pagination={{ pageSize: 20 }}
                    columns={[
                      { title: t('subjectCode'), dataIndex: 'code', width: 120 },
                      { title: t('subjectName'), dataIndex: 'name' },
                      {
                        title: t('subjectHod', { defaultValue: 'Subject HOD' }),
                        key: 'hod',
                        ellipsis: true,
                        render: (_: unknown, row: SubjectRow) => hodLabel(row),
                      },
                      {
                        title: t('offerings', { defaultValue: 'Offerings' }),
                        dataIndex: 'offering_count',
                        width: 110,
                        render: (v?: number) => v ?? 0,
                      },
                      ...(canManage
                        ? [
                            {
                              title: t('actions', { defaultValue: 'Actions' }),
                              key: 'actions',
                              width: 180,
                              render: (_: unknown, row: SubjectRow) => (
                                <Space>
                                  <Button size="small" onClick={() => openSubjectEdit(row)}>
                                    {tc('edit')}
                                  </Button>
                                  <Popconfirm
                                    title={t('confirmDeleteSubject', {
                                      defaultValue: 'Delete this subject? Offerings must be removed first.',
                                    })}
                                    onConfirm={() => deleteSubject.mutate(row.id)}
                                  >
                                    <Button size="small" danger loading={deleteSubject.isPending}>
                                      {tc('delete')}
                                    </Button>
                                  </Popconfirm>
                                </Space>
                              ),
                            },
                          ]
                        : []),
                    ]}
                  />
                </Card>
                <Card size="small" title={t('offerings', { defaultValue: 'Offerings' })}>
                  <Table
                    rowKey="id"
                    size="small"
                    loading={offerings.isLoading}
                    dataSource={offerings.data ?? []}
                    pagination={{ pageSize: 20 }}
                    locale={{ emptyText: t('noOfferingsYet', { defaultValue: 'No offerings yet' }) }}
                    columns={[
                      { title: t('session'), dataIndex: 'session_code', width: 120 },
                      { title: t('grade'), dataIndex: 'grade_code', width: 100 },
                      { title: t('subjectCode'), dataIndex: 'subject_code', width: 120 },
                      { title: t('subjectName'), dataIndex: 'subject_name' },
                      {
                        title: t('elective', { defaultValue: 'Elective' }),
                        dataIndex: 'is_elective',
                        width: 100,
                        render: (v: number | boolean) => (v ? <Tag color="blue">Yes</Tag> : '—'),
                      },
                      ...(canManage
                        ? [
                            {
                              title: t('actions', { defaultValue: 'Actions' }),
                              key: 'actions',
                              width: 180,
                              render: (_: unknown, row: OfferingRow) => (
                                <Space>
                                  <Button size="small" onClick={() => openOfferingEdit(row)}>
                                    {tc('edit')}
                                  </Button>
                                  <Popconfirm
                                    title={t('confirmDeleteOffering', {
                                      defaultValue: 'Delete this offering? Teacher assignments must be removed first.',
                                    })}
                                    onConfirm={() => deleteOffering.mutate(row.id)}
                                  >
                                    <Button size="small" danger loading={deleteOffering.isPending}>
                                      {tc('delete')}
                                    </Button>
                                  </Popconfirm>
                                </Space>
                              ),
                            },
                          ]
                        : []),
                    ]}
                  />
                </Card>
              </Space>
            ),
          },
          {
            key: 'teachers',
            label: t('teacherAssignments', { defaultValue: 'Teacher assignments' }),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert
                  type="info"
                  showIcon
                  message={t('teachersTabHint', {
                    defaultValue:
                      'Staff directory shows linked employees. Section–subject assignments control who may mark attendance for a class.',
                  })}
                  action={
                    <Space>
                      <Button size="small" onClick={() => navigate('/education/students?view=teachers')}>
                        {t('staffDirectory', { defaultValue: 'Staff directory' })}
                      </Button>
                      {sessionFilter || activeSession ? (
                        <Button
                          size="small"
                          onClick={() => openSessionWorkspace(sessionFilter || activeSession!.id, 'teachers')}
                        >
                          {t('assignInSession', { defaultValue: 'Assign in session' })}
                        </Button>
                      ) : null}
                    </Space>
                  }
                />
                <Space wrap>
                  <EducationSessionSelect
                    allowClear
                    placeholder={t('filterBySession', { defaultValue: 'Filter assignments by session' })}
                    value={sessionFilter}
                    onChange={(value) => setSessionFilter(value || undefined)}
                    style={{ width: 320 }}
                  />
                  {canManage ? (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        teacherForm.resetFields();
                        setTeacherModal(true);
                      }}
                    >
                      {t('assignTeacher', { defaultValue: 'Assign teacher' })}
                    </Button>
                  ) : null}
                </Space>
                <Card size="small" title={t('staffDirectory', { defaultValue: 'Staff directory' })}>
                  <Table
                    rowKey="id"
                    size="small"
                    loading={staff.isLoading}
                    dataSource={staff.data ?? []}
                    pagination={{ pageSize: 15 }}
                    columns={[
                      { title: t('employeeCode', { defaultValue: 'Employee code' }), dataIndex: 'employee_code', width: 130 },
                      {
                        title: t('name', { defaultValue: 'Name' }),
                        key: 'name',
                        render: (_: unknown, row: { first_name?: string; last_name?: string }) =>
                          [row.first_name, row.last_name].filter(Boolean).join(' ') || '—',
                      },
                      {
                        title: t('sectionAssignments', { defaultValue: 'Section subjects' }),
                        dataIndex: 'section_assignment_count',
                        width: 140,
                        render: (v?: number) => v ?? 0,
                      },
                      {
                        title: t('studentAssignments', { defaultValue: 'Student links' }),
                        dataIndex: 'student_assignment_count',
                        width: 120,
                        render: (v?: number) => v ?? 0,
                      },
                      {
                        title: t('status'),
                        dataIndex: 'is_active',
                        width: 100,
                        render: (v?: number) =>
                          Number(v) === 1 ? <Tag color="green">{t('active', { defaultValue: 'Active' })}</Tag> : <Tag>Inactive</Tag>,
                      },
                    ]}
                  />
                </Card>
                <Card size="small" title={t('sectionSubjectTeachers', { defaultValue: 'Section × subject teachers' })}>
                  <Table
                    rowKey="id"
                    size="small"
                    loading={teachers.isLoading}
                    dataSource={teachers.data ?? []}
                    pagination={{ pageSize: 20 }}
                    locale={{ emptyText: t('noTeacherAssignments', { defaultValue: 'No assignments yet' }) }}
                    columns={[
                      { title: t('session'), dataIndex: 'session_code', width: 120 },
                      {
                        title: t('section'),
                        key: 'section',
                        render: (_: unknown, row: TeacherRow) => `${row.grade_code || ''} ${row.section_name || ''}`.trim() || '—',
                      },
                      {
                        title: t('subject', { defaultValue: 'Subject' }),
                        key: 'subject',
                        render: (_: unknown, row: TeacherRow) => `${row.subject_code || ''} — ${row.subject_name || ''}`.trim(),
                      },
                      {
                        title: t('teacher', { defaultValue: 'Teacher' }),
                        key: 'teacher',
                        render: (_: unknown, row: TeacherRow) =>
                          [row.employee_code, [row.first_name, row.last_name].filter(Boolean).join(' ')].filter(Boolean).join(' — ') ||
                          '—',
                      },
                      ...(canManage
                        ? [
                            {
                              title: t('actions', { defaultValue: 'Actions' }),
                              key: 'actions',
                              width: 120,
                              render: (_: unknown, row: TeacherRow) => (
                                <Popconfirm
                                  title={t('confirmUnassignTeacher', {
                                    defaultValue: 'Remove this teacher assignment?',
                                  })}
                                  onConfirm={() => unassignTeacher.mutate(row.id)}
                                >
                                  <Button size="small" danger loading={unassignTeacher.isPending}>
                                    {tc('delete')}
                                  </Button>
                                </Popconfirm>
                              ),
                            },
                          ]
                        : []),
                    ]}
                  />
                </Card>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={gradeModal === 'create' ? t('addGrade') : t('editGrade', { defaultValue: 'Edit grade' })}
        open={Boolean(gradeModal)}
        onCancel={() => setGradeModal(null)}
        footer={null}
        destroyOnClose
      >
        <Form form={gradeForm} layout="vertical" onFinish={(values) => saveGrade.mutate(values)}>
          <Form.Item name="code" label={t('gradeCode')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('gradeName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="sequence_no" label={t('sequence')} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saveGrade.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>

      <Modal
        title={sectionModal === 'create' ? t('addSection') : t('editSection', { defaultValue: 'Edit section' })}
        open={Boolean(sectionModal)}
        onCancel={() => setSectionModal(null)}
        footer={null}
        destroyOnClose
      >
        <Form form={sectionForm} layout="vertical" onFinish={(values) => saveSection.mutate(values)}>
          {sectionModal === 'create' ? (
            <Form.Item name="session_id" label={t('session')} rules={[{ required: true }]}>
              <EducationSessionSelect style={{ width: '100%' }} />
            </Form.Item>
          ) : null}
          <Form.Item name="grade_level_id" label={t('grade')} rules={[{ required: true }]}>
            <EducationGradeSelect
              ensureOption={
                sectionModal && sectionModal !== 'create' && sectionModal.grade_level_id
                  ? {
                      value: sectionModal.grade_level_id,
                      label:
                        [sectionModal.grade_code, sectionModal.grade_name].filter(Boolean).join(' — ') ||
                        sectionModal.grade_level_id,
                    }
                  : undefined
              }
            />
          </Form.Item>
          <Form.Item name="name" label={t('sectionName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="capacity" label={t('capacity')} rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saveSection.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>

      <Modal
        title={subjectModal === 'create' ? t('addSubject') : t('editSubject', { defaultValue: 'Edit subject' })}
        open={Boolean(subjectModal)}
        onCancel={() => setSubjectModal(null)}
        footer={null}
        destroyOnClose
      >
        <Form form={subjectForm} layout="vertical" onFinish={(values) => saveSubject.mutate(values)}>
          <Form.Item name="code" label={t('subjectCode')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('subjectName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="hod_staff_link_id"
            label={t('subjectHod', { defaultValue: 'Subject HOD' })}
            extra={t('subjectHodHint', {
              defaultValue: 'Optional. Choose a linked teacher as head of this subject.',
            })}
          >
            <EducationStaffSelect
              allowClear
              placeholder={t('selectSubjectHod', { defaultValue: 'Select teacher (HOD)' })}
              ensureOption={
                subjectModal && subjectModal !== 'create' && subjectModal.hod_staff_link_id
                  ? {
                      value: subjectModal.hod_staff_link_id,
                      label: hodLabel(subjectModal),
                    }
                  : undefined
              }
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saveSubject.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>

      <Modal
        title={
          offeringModal === 'create'
            ? t('addOffering', { defaultValue: 'Add offering' })
            : t('editOffering', { defaultValue: 'Edit offering' })
        }
        open={Boolean(offeringModal)}
        onCancel={() => setOfferingModal(null)}
        footer={null}
        destroyOnClose
      >
        <Form form={offeringForm} layout="vertical" onFinish={(values) => saveOffering.mutate(values)}>
          {!editingOffering ? (
            <>
              <Form.Item name="session_id" label={t('session')} rules={[{ required: true }]}>
                <EducationSessionSelect style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="grade_level_id" label={t('grade')} rules={[{ required: true }]}>
                <EducationGradeSelect />
              </Form.Item>
              <Form.Item name="subject_id" label={t('subject', { defaultValue: 'Subject' })} rules={[{ required: true }]}>
                <EducationSubjectSelect />
              </Form.Item>
            </>
          ) : null}
          <Form.Item name="is_elective" valuePropName="checked">
            <Checkbox>{t('elective', { defaultValue: 'Elective' })}</Checkbox>
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saveOffering.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>

      <Modal
        title={t('assignTeacher', { defaultValue: 'Assign teacher' })}
        open={teacherModal}
        onCancel={() => setTeacherModal(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={teacherForm} layout="vertical" onFinish={(values) => assignTeacher.mutate(values)}>
          <Form.Item name="section_id" label={t('section')} rules={[{ required: true }]}>
            <EducationSectionSelect sessionId={sessionFilter || activeSession?.id} />
          </Form.Item>
          <Form.Item name="subject_id" label={t('subject', { defaultValue: 'Subject' })} rules={[{ required: true }]}>
            <EducationSubjectSelect />
          </Form.Item>
          <Form.Item name="staff_link_id" label={t('teacher', { defaultValue: 'Teacher' })} rules={[{ required: true }]}>
            <EducationStaffSelect />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={assignTeacher.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>
    </EducationPageChrome>
  );
}
